/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';

import announce from '@Utils/announce';
import { commaFormatNumber } from '@Utils/String';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import history from '@AppRoutes/history';
import { mapContextToTitle } from '@Containers/ResultToolbar/ResultToolbarConstants';

import useGridView from '@Pages/Results/hooks/useGridView';
import useAvailableDatabases from '@Pages/Results/hooks/useAvailableDatabases';
import useResultsMetadata from '@Pages/Results/hooks/useResultsMetadata';
import useSearchObjectFromStorage from '@Pages/Results/hooks/useSearchObjectFromStorage';
import useCurrentPageSize from '@Pages/Results/hooks/useCurrentPageSize';
import useSupportsReactions from '@Pages/Results/hooks/useSupportsReactions';
import { useToggleConditionsGlobal } from '@Pages/Results/ToggleConditionsGlobalContext';
import useEffectWhenStateParamsReady from '@Pages/Results/hooks/useEffectWhenStateParamsReady';

import { trackSearchResults } from './analytics';
import ResultsPage from './ResultsPage';

/*
 * !IMPORTANT: Be careful when using params as effect dependency, the whole object reference changes sometimes, even if values are the same.
 * Prefer using specific params values instead of the whole params object.
 */
function ResultsContainer({
  params,
  activeBreadcrumb,
  stateParamsReady,
  relativeContexts,
  getRelativeContexts,
  checkReopenExport,
  initResultsStateForParams,
  cleanResultsState,
  fetchResultsItems,
  resultsItems,
  resultsFinished,
  resultsPerPage,
  updateResultsPerPage,
  paginationVisible,
  selectDatabase,
  goToResultPage,
  showAllPreparations,
  recordCountOrTotalResult,
  updateCheckedItemsInBreadcrumb,
  isLimitDisabled,
  limitTo,
  isExportEnabled,
  openExportDialog,

  // Selection
  checkedItems,
  isAllResultsChecked,
  isAllResultsCheckedPartial,
  setAllResultsChecked,
  setAllResultsUnchecked,
  checkedItemsNumber,
  breadcrumbCheckedItemsNumber,
}) {
  const [currentSelectedItems, setCurrentSelectedItems] = useState(breadcrumbCheckedItemsNumber);

  const hasSeenInitialResultsRef = useRef(false);
  const prevResultsLoadingRef = useRef(false);
  const pendingScrollResetRef = useRef(false);
  const isInitialPageRef = useRef(true);

  useEffect(() => {
    const resultsLoading = !resultsFinished;
    let announceTimer;

    if (resultsLoading && hasSeenInitialResultsRef.current && !prevResultsLoadingRef.current) {
      announce('Loading results');
    }

    if (!resultsLoading && recordCountOrTotalResult != null) {
      if (prevResultsLoadingRef.current && hasSeenInitialResultsRef.current) {
        const resultLabel = recordCountOrTotalResult === 1 ? 'result' : 'results';
        const message =
          recordCountOrTotalResult === 0
            ? `No results found for ${mapContextToTitle[params.context]}`
            : `Results updated. ${commaFormatNumber(recordCountOrTotalResult)} ${resultLabel} available.`;

        announceTimer = setTimeout(() => {
          announce(message);
        }, 2500);
      }
      hasSeenInitialResultsRef.current = true;
    }
    prevResultsLoadingRef.current = resultsLoading;

    return () => {
      clearTimeout(announceTimer);
    };
  }, [resultsFinished, recordCountOrTotalResult, params.context]);

  // Mark focus/scroll as pending when the page or per-page value changes.
  useEffect(() => {
    if (isInitialPageRef.current) {
      isInitialPageRef.current = false;
      return;
    }

    pendingScrollResetRef.current = true;
  }, [params.pageNumber, resultsPerPage]);

  // Move focus and reset scroll once the pending navigation's results have finished loading.
  useEffect(() => {
    if (!resultsFinished || !pendingScrollResetRef.current) return;
    pendingScrollResetRef.current = false;

    const listEl = document.querySelector('.e2e-results-list');
    if (listEl) {
      listEl.focus({ preventScroll: true });
    }

    const resultsPage = document.getElementById('results-page');
    if (resultsPage) {
      resultsPage.scrollTop = 0;
    }
  }, [resultsFinished]);

  useEffectWhenStateParamsReady(
    () => {
      if (!relativeContexts) {
        getRelativeContexts(params, history);
      }

      if (relativeContexts) {
        checkReopenExport(params, { relativeContexts });
      }
    },
    [params.databaseId, params.resultName, relativeContexts],
    stateParamsReady
  );

  useEffectWhenStateParamsReady(
    () => {
      initResultsStateForParams(params);
      fetchResultsItems(params, activeBreadcrumb);

      return () => {
        cleanResultsState(params);
      };
    },
    [params.databaseId, params.resultName, params.sort, params.order, params.pageNumber],
    stateParamsReady
  );
  useEffect(() => {
    updateResultsPerPage({ resultsPerPage }, params, activeBreadcrumb);
  }, [resultsPerPage]);

  useEffect(() => {
    if (stateParamsReady) {
      setCurrentSelectedItems(checkedItemsNumber);
      updateCheckedItemsInBreadcrumb(params, checkedItems);
    }
  }, [checkedItems]);

  useEffect(() => {
    // This to focus the skip link when the results are loaded
    RouteChangeTracking.handleSkipLinkFocus();
  }, []);

  const [isGridView, toggleGridView] = useGridView(activeBreadcrumb);
  const { availableDatabases, currentDatabase, isDbSwitcherVisible } = useAvailableDatabases(params, activeBreadcrumb);
  const metadata = useResultsMetadata(params);
  const [searchObject] = useSearchObjectFromStorage(params);
  const currentPageSize = useCurrentPageSize(params, activeBreadcrumb);
  const supportsReactions = useSupportsReactions(params);
  const [showConditions, toggleConditions] = useToggleConditionsGlobal(params);

  const currentPage = parseInt(params.pageNumber, 10) || 1;
  const totalPages = resultsPerPage > 0 ? Math.ceil(recordCountOrTotalResult / resultsPerPage) : 0;

  // AA
  useEffectWhenStateParamsReady(
    () => {
      if (recordCountOrTotalResult) {
        trackSearchResults({
          params,
          currentPageSize,
          totalResults: recordCountOrTotalResult,
          activeBreadcrumb,
        });
      }
    },
    [recordCountOrTotalResult],
    stateParamsReady
  );

  // Callbacks to prevent unnecessary toolbar rerenders
  const goToResultPageCallback = useCallback((newParams) => goToResultPage(newParams, params), [params]);
  const showAllPreparationsCallback = useCallback(() => showAllPreparations(params), [params]);
  const limitToCallback = useCallback((options) => limitTo(options, params, activeBreadcrumb), [limitTo, params, activeBreadcrumb]);
  const openExportDialogCallback = useCallback(
    (tpd) => openExportDialog(tpd, params, { resultSize: recordCountOrTotalResult, relativeContexts }),
    [params, recordCountOrTotalResult, relativeContexts]
  );

  return (
    <ResultsPage
      stateParamsReady={stateParamsReady}
      params={params}
      isGridView={isGridView}
      toggleGridView={toggleGridView}
      availableDatabases={availableDatabases}
      currentDatabase={currentDatabase}
      isDbSwitcherVisible={isDbSwitcherVisible}
      selectDatabase={selectDatabase}
      goToResultPage={goToResultPageCallback}
      metadata={metadata}
      context={params.context}
      results={resultsItems}
      searchType={searchObject && searchObject.searchType}
      originalSearchType={searchObject && (searchObject.originalSearchType || searchObject.searchType)}
      currentPageSize={currentPageSize}
      resultsLoading={!resultsFinished}
      paginationVisible={paginationVisible}
      supportsReactions={supportsReactions}
      showAllPreparations={showAllPreparationsCallback}
      showConditions={showConditions}
      toggleConditions={toggleConditions}
      recordCountOrTotalResult={recordCountOrTotalResult}
      isLimitDisabled={isLimitDisabled}
      limitTo={limitToCallback}
      isExportEnabled={isExportEnabled}
      openExportDialog={openExportDialogCallback}
      isAllResultsChecked={isAllResultsChecked}
      isAllResultsCheckedPartial={isAllResultsCheckedPartial}
      setAllResultsChecked={setAllResultsChecked}
      setAllResultsUnchecked={setAllResultsUnchecked}
      checkedItemsNumber={currentSelectedItems}
      activeBreadcrumb={activeBreadcrumb}
      currentPage={currentPage}
      totalPages={totalPages}
    />
  );
}

ResultsContainer.propTypes = {
  params: PropTypes.object.isRequired,
  activeBreadcrumb: PropTypes.object.isRequired, // from HOC
  stateParamsReady: PropTypes.bool, // from HOC

  relativeContexts: PropTypes.instanceOf(Map),
  getRelativeContexts: PropTypes.func.isRequired,
  checkReopenExport: PropTypes.func.isRequired,

  initResultsStateForParams: PropTypes.func.isRequired,
  cleanResultsState: PropTypes.func.isRequired,
  fetchResultsItems: PropTypes.func.isRequired,
  resultsItems: PropTypes.array.isRequired,
  resultsFinished: PropTypes.bool,
  resultsPerPage: PropTypes.number,
  updateResultsPerPage: PropTypes.func.isRequired,
  paginationVisible: PropTypes.bool,
  selectDatabase: PropTypes.func.isRequired,
  goToResultPage: PropTypes.func.isRequired,
  showAllPreparations: PropTypes.func.isRequired,
  recordCountOrTotalResult: PropTypes.number,
  updateCheckedItemsInBreadcrumb: PropTypes.func.isRequired,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func.isRequired,
  isExportEnabled: PropTypes.bool,
  openExportDialog: PropTypes.func.isRequired,

  // Selection
  checkedItems: PropTypes.object,
  isAllResultsChecked: PropTypes.bool,
  isAllResultsCheckedPartial: PropTypes.bool,
  setAllResultsChecked: PropTypes.func.isRequired,
  setAllResultsUnchecked: PropTypes.func.isRequired,
  checkedItemsNumber: PropTypes.number,
  breadcrumbCheckedItemsNumber: PropTypes.number,
};

export default ResultsContainer;
